import React, {useCallback} from 'react';
import {useServerProps} from '@shopify/hydrogen';
import {
  Menu,
  MenuItem,
  MoneyV2,
  UserError,
} from '@shopify/hydrogen/storefront-api-types';
import {getTreeFromFlatData} from '@nosferatu500/react-sortable-tree';

import sanityClient from '@sanity/client';
import sanityConfig from '../../sanity.config';

import {
  SanityPageMenuLinkItem,
  SanityCollectionTree,
} from '~/types/sanityTypes';

// @ts-expect-error types not available
import typographicBase from 'typographic-base';

/**
 * This is a hack until we have better built-in primitives for
 * causing server components to re-render.
 *
 * @returns function when called will cause the current page to re-render on the server
 */
export function useRenderServerComponents() {
  const {serverProps, setServerProps} = useServerProps();

  return useCallback(() => {
    setServerProps('renderRsc', !serverProps.renderRsc);
  }, [serverProps, setServerProps]);
}

export function missingClass(string?: string, prefix?: string) {
  if (!string) {
    return true;
  }

  const regex = new RegExp(` ?${prefix}`, 'g');
  return string.match(regex) === null;
}

export function formatText(input?: string | React.ReactNode) {
  if (!input) {
    return;
  }

  if (typeof input !== 'string') {
    return input;
  }

  return typographicBase(input, {locale: 'en-us'}).replace(
    /\s([^\s<]+)\s*$/g,
    '\u00A0$1',
  );
}

export function isNewArrival(date: string, daysOld = 30) {
  return (
    new Date(date).valueOf() >
    new Date().setDate(new Date().getDate() - daysOld).valueOf()
  );
}

export function isDiscounted(price: MoneyV2, compareAtPrice: MoneyV2) {
  if (Number(compareAtPrice?.amount) > Number(price?.amount)) {
    return true;
  }
  return false;
}

export function getExcerpt(text: string) {
  const regex = /<p.*>(.*?)<\/p>/;
  const match = regex.exec(text);
  return match?.length ? match[0] : text;
}

export function resolvePath(type: string, slug: string) {
  switch (type) {
    case 'page':
      return '/sivu/' + slug;
    case 'product':
      return '/tuote/' + slug;
    case 'collection':
      return '/kategoria/' + slug;
    case 'category':
      return '/kategoria/' + slug;
    case 'caseStudy':
      return '/referenssi/' + slug;
    case 'service':
      return '/palvelu/' + slug;
    case 'topLevel':
      return '/' + slug;
    default:
      return `${type}/${slug}`;
  }
}

function resolveInternalToFromType(
  {
    customPrefixes,
    pathname,
    type,
  }: {
    customPrefixes: Record<string, string>;
    pathname?: string;
    type?: string;
  } = {
    customPrefixes: {},
  },
) {
  if (!pathname || !type) return '';

  /*
    MenuItemType enum
    @see: https://shopify.dev/api/storefront/unstable/enums/MenuItemType
  */
  const defaultPrefixes = {
    BLOG: 'blogs',
    COLLECTION: 'kategoria',
    COLLECTIONS: 'collections', // Collections All (not documented)
    FRONTPAGE: 'frontpage',
    HTTP: '',
    PAGE: 'sivu',
    CATALOG: 'collections/all', // Products All
    PRODUCT: 'tuote',
    SEARCH: 'search',
    SHOP_POLICY: 'policies',
  };

  const pathParts = pathname.split('/');
  const handle = pathParts.pop() || '';
  const routePrefix: Record<string, string> = {
    ...defaultPrefixes,
    ...customPrefixes,
  };

  switch (true) {
    // special cases
    case type === 'FRONTPAGE':
      return '/';

    case type === 'ARTICLE': {
      const blogHandle = pathParts.pop();
      return routePrefix.BLOG
        ? `/${routePrefix.BLOG}/${blogHandle}/${handle}/`
        : `/${blogHandle}/${handle}/`;
    }

    case type === 'COLLECTIONS':
      return `/${routePrefix.COLLECTIONS}`;

    case type === 'SEARCH':
      return `/${routePrefix.SEARCH}`;

    case type === 'CATALOG':
      return `/${routePrefix.CATALOG}`;

    // common cases: BLOG, PAGE, COLLECTION, PRODUCT, SHOP_POLICY, HTTP
    default:
      return routePrefix[type]
        ? `/${routePrefix[type]}/${handle}`
        : `/${handle}`;
  }
}

/*
  Parse each menu link and adding, isExternal, to and target
*/
function parseItem(customPrefixes = {}) {
  return function (item: MenuItem): EnhancedMenuItem {
    if (!item?.url || !item?.type) {
      // eslint-disable-next-line no-console
      console.warn('Invalid menu item. Must include a url and type.');
      // @ts-ignore
      return;
    }

    // extract path from url because we don't need the origin on internal to attributes
    const {pathname} = new URL(item.url);

    /*
      Currently the MenuAPI only returns online store urls e.g — xyz.myshopify.com/..
      Note: update logic when API is updated to include the active qualified domain
    */
    const isInternalLink = /\.myshopify\.com/g.test(item.url);

    const parsedItem = isInternalLink
      ? // internal links
        {
          ...item,
          isExternal: false,
          target: '_self',
          to: resolveInternalToFromType({
            type: item.type,
            customPrefixes,
            pathname,
          }),
        }
      : // external links
        {
          ...item,
          isExternal: true,
          target: '_blank',
          to: item.url,
        };

    return {
      ...parsedItem,
      items: item.items?.map(parseItem(customPrefixes)),
    };
  };
}

export interface EnhancedMenuItem extends MenuItem {
  to: string;
  target: string;
  isExternal?: boolean;
  items: EnhancedMenuItem[];
}

export interface EnhancedMenu extends Menu {
  items: EnhancedMenuItem[];
}

/*
  Recursively adds `to` and `target` attributes to links based on their url
  and resource type.
  It optionally overwrites url paths based on item.type
*/
export function parseMenu(menu: Menu, customPrefixes = {}): EnhancedMenu {
  if (!menu?.items) {
    // eslint-disable-next-line no-console
    console.warn('Invalid menu passed to parseMenu');
    // @ts-ignore
    return menu;
  }

  return {
    ...menu,
    items: menu.items.map(parseItem(customPrefixes)),
  };
}

export function getApiErrorMessage(
  field: string,
  data: Record<string, any>,
  errors: UserError[],
) {
  if (errors?.length) return errors[0].message ?? errors[0];
  if (data?.[field]?.customerUserErrors?.length)
    return data[field].customerUserErrors[0].message;
  return null;
}

export function statusMessage(status: string) {
  const translations: Record<string, string> = {
    ATTEMPTED_DELIVERY: 'Attempted delivery',
    CANCELED: 'Peruttu',
    CONFIRMED: 'Vahvistettu',
    DELIVERED: 'Toimitettu',
    FAILURE: 'Epäonnistunut',
    FULFILLED: 'Valmis',
    IN_PROGRESS: 'Käsittelyssä',
    IN_TRANSIT: 'Matkalla',
    LABEL_PRINTED: 'Label printed',
    LABEL_PURCHASED: 'Label purchased',
    LABEL_VOIDED: 'Label voided',
    MARKED_AS_FULFILLED: 'Merkitty valmiiksi',
    NOT_DELIVERED: 'Ei toimitettu',
    ON_HOLD: 'Pidossa',
    OPEN: 'Avoinna',
    OUT_FOR_DELIVERY: 'Out for delivery',
    PARTIALLY_FULFILLED: 'Osittain valmis',
    PENDING_FULFILLMENT: 'Odottaa',
    PICKED_UP: 'Näkyy toimitettuna',
    READY_FOR_PICKUP: 'Valmis noudettavaksi',
    RESTOCKED: 'Restocked',
    SCHEDULED: 'Aikataulutettu',
    SUBMITTED: 'Submitted',
    UNFULFILLED: 'Unfulfilled',
  };
  try {
    return translations?.[status];
  } catch (error) {
    return status;
  }
}

export function paymentStatusMessage(status: string) {
  const translations: Record<string, string> = {
    PAID: 'Maksettu',
    PENDING: 'Odottaa',
    AUTHORIZED: 'Authorized',
    PARTIALLY_PAID: 'Osittain maksettu',
    PARTIALLY_REFUNDED: 'Osittain hyvitetty',
    REFUNDED: 'Hyvitetty',
    VOIDED: 'Voided',
    CANCELLED: 'Peruttu',
  };
  try {
    return translations?.[status];
  } catch (error) {
    return status;
  }
}

export function emailValidation(email: HTMLInputElement) {
  if (email.validity.valid) return null;

  return email.validity.valueMissing
    ? 'Please enter an email'
    : 'Please enter a valid email';
}

export function passwordValidation(password: HTMLInputElement) {
  if (password.validity.valid) return null;

  if (password.validity.valueMissing) {
    return 'Please enter a password';
  }

  return 'Password must be at least 6 characters';
}

export const useSanityClient = sanityClient(sanityConfig);
export const useSanityPreviewClient = sanityClient({
  ...sanityConfig,
  withCredentials: true,
  useCdn: false,
});

export function sanityFlatDataToTree(
  data: SanityCollectionTree[],
): SanityCollectionTree[] {
  return getTreeFromFlatData({
    flatData: data.map((item) => ({
      ...item,
      // if parent: undefined, the tree won't be constructed
      parent: item.parent || null,
    })),
    getKey: (item) => item._key,
    // @ts-ignore
    getParentKey: (item) => item.parent,
    // without rootKey, the tree won't be constructed
    rootKey: null as any,
  }) as SanityCollectionTree[];
}

export function findCollectionItemById(
  id: string,
  treeItems: SanityCollectionTree[],
): SanityCollectionTree | null {
  let result = null;

  for (let index = 0; index < treeItems.length; index++) {
    if (treeItems[index].value?._id === id) {
      result = treeItems[index];
      break;
    } else if (treeItems[index].children && treeItems[index].children?.length) {
      const childResult = findCollectionItemById(
        id,
        treeItems[index].children as SanityCollectionTree[],
      );
      if (childResult) {
        result = childResult;
        break;
      }
    }
  }

  return result;
}

export function findCollectionItemBySlug(
  slug: string,
  treeItems: SanityCollectionTree[],
): SanityCollectionTree | null {
  let result = null;

  for (let index = 0; index < treeItems.length; index++) {
    if (treeItems[index].value?.slug === slug) {
      result = treeItems[index];
      break;
    } else if (treeItems[index].children && treeItems[index].children?.length) {
      const childResult = findCollectionItemBySlug(
        slug,
        treeItems[index].children as SanityCollectionTree[],
      );
      if (childResult) {
        result = childResult;
        break;
      }
    }
  }

  return result;
}

export function findCollectionItemByKey(
  key: string,
  treeItems: SanityCollectionTree[],
): SanityCollectionTree | null {
  let result = null;

  for (let index = 0; index < treeItems.length; index++) {
    if (treeItems[index]._key === key) {
      result = treeItems[index];
      break;
    } else if (treeItems[index].children && treeItems[index].children?.length) {
      const childResult = findCollectionItemByKey(
        key,
        treeItems[index].children as SanityCollectionTree[],
      );
      if (childResult) {
        result = childResult;
        break;
      }
    }
  }

  return result;
}

export function findCollectionRootParentByKey(
  parentKey: string,
  treeItems: SanityCollectionTree[],
): SanityCollectionTree | null {
  let parent = findCollectionItemByKey(parentKey, treeItems);

  if (parent?.parent) {
    parent = findCollectionItemByKey(parent.parent, treeItems);
  }

  return parent;
}

export function getSlugsFromCollectionTreeArray(
  treeItems: SanityCollectionTree[],
): string[] {
  const result: string[] = [];

  treeItems.forEach((item) => {
    result.push(item.value.slug);

    if (item.children) {
      result.push(...getSlugsFromCollectionTreeArray(item.children));
    }
  });

  return result;
}

export function getTitlesFromCollectionTreeArray(
  treeItems: SanityCollectionTree[],
): string[] {
  const result: string[] = [];

  treeItems.forEach((item) => {
    result.push(item.value.title);

    if (item.children) {
      result.push(...getTitlesFromCollectionTreeArray(item.children));
    }
  });

  return result;
}

export function convertCollectionTreeToInternalLinkArray(
  collectionTree: SanityCollectionTree[],
  vendorsCollectionSlug?: string,
): SanityPageMenuLinkItem[] {
  // If collection is deleted on Shopify, value will be undefined.
  return collectionTree
    .filter((treeItem) => !!treeItem.value)
    .map((treeItem) => {
      // Handle special case for vendors collection
      if (vendorsCollectionSlug === treeItem.value.slug) {
        return {
          documentType: 'topLevel',
          link: {
            _key: treeItem._key,
            _type: 'linkInternal',
            documentType: 'topLevel',
            title: treeItem.value.title,
            slug: treeItem.value.slug,
          },
        };
      }

      return {
        documentType: 'collection',
        link: {
          _key: treeItem._key,
          _type: 'linkInternal',
          documentType: 'collection',
          title: treeItem.value.title,
          slug: treeItem.value.slug,
        },
        ...(treeItem.children?.length && {
          children: convertCollectionTreeToInternalLinkArray(treeItem.children),
        }),
      };
    });
}

export function findCollectionTreeBranchBySlug(
  slug: string,
  treeItems: SanityCollectionTree[],
): SanityCollectionTree[] | null {
  const branchTip = findCollectionItemBySlug(
    slug,
    treeItems as SanityCollectionTree[],
  );

  if (!branchTip) {
    return null;
  }

  if (!branchTip.parent) {
    return [branchTip];
  }

  const result = [];
  result.push({...branchTip, children: undefined});
  let foundParent: SanityCollectionTree | null = branchTip;

  while (foundParent) {
    if (foundParent.parent) {
      foundParent = findCollectionItemByKey(foundParent.parent, treeItems);
      if (foundParent) {
        result.push({...foundParent, children: undefined});
      } else {
        break;
      }
    } else {
      foundParent = null;
    }
  }

  return result.reverse();
}

/**
 * Helper function to return the correct version of the document
 * If we're in "preview mode" and have multiple documents, return the draft
 */
export default function filterDataToSingleItem(data: any, preview: boolean) {
  if (!Array.isArray(data)) {
    return data;
  }

  if (preview) {
    return data.find((item) => item._id.startsWith(`drafts.`)) || data[0];
  }

  return data[0];
}

export function getCookie(cname: string) {
  const name = cname + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

export const translateProductOptionName = (name: string) => {
  switch (name) {
    case 'Size':
      return 'Koko';
    case 'Color':
      return 'Väri';
    case 'Material':
      return 'Materiaali';
    case 'Style':
      return 'Tyyli';
    default:
      return name;
  }
};
